import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Form, Input, Select,
} from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const warnedComponents = new Set();

const FIELD_COMPONENTS = new Set(['select', 'checkbox', 'text', 'textarea']);

// ENG-3760. A provider field may declare `rule_default_strategies`: instead of a
// fixed person, the gateway resolves the assignee at upload time. The strategy
// is stored as `provider_strategy`, and the field's own value becomes the
// fallback assignee.
const STRATEGY_KEY = 'provider_strategy';
const STRATEGY_VALUE_PREFIX = 'strategy:';
const STRATEGIES = {
  ordering_provider: {
    label: 'Ordering provider (from the document)',
    fallbackHelp: 'Used when neither the ordering provider nor the referring provider on the document matches a single active eCW provider, and the patient has no checked-out visit (or checked-in telehealth visit) with an active provider. Leave blank to fail the upload instead.',
  },
  pcp: {
    label: 'Patient\'s PCP (from the chart)',
    fallbackHelp: 'Used when the chart has no active PCP and the patient has no checked-out visit (or checked-in telehealth visit) with an active provider. Leave blank to fail the upload instead.',
  },
};

const fieldStrategies = (field) => (field.rule_default_strategies || []).filter((strategy) => STRATEGIES[strategy]);

const withoutField = (value, fieldName) => {
  const next = { ...value };
  delete next[fieldName];
  return next;
};

const fieldExtra = (field) => {
  if (field.component === 'checkbox') {
    return 'Inherit keeps today\'s behavior: the box is left unchecked in eCW.';
  }
  if (fieldStrategies(field).length > 0) {
    return 'Choose a specific eCW user, or let Medsender assign the document\'s ordering provider or the patient\'s PCP at upload time.';
  }
  if (field.internal_name === 'notes') {
    return 'Sent to eCW as \'Auto Uploaded by Medsender: <your text>\'. Leave empty to send the attribution only, or reset to send no description.';
  }
  return field.help_text;
};

const RuleDocumentDefaultsFields = ({
  fields, value, onChange, credentialId, providerError,
}) => {
  const [optionsBySource, setOptionsBySource] = useState({});
  const [loadingSources, setLoadingSources] = useState({});
  const [failedSources, setFailedSources] = useState({});

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const sources = [...new Set(fields.flatMap((field) => (field.options_source ? [field.options_source] : [])))];
    if (!credentialId || sources.length === 0) return undefined;

    const loadOptions = async () => {
      setLoadingSources(Object.fromEntries(sources.map((source) => [source, true])));
      setFailedSources((current) => ({
        ...current,
        ...Object.fromEntries(sources.map((source) => [source, false])),
      }));
      await Promise.all(sources.map(async (source) => {
        try {
          const response = await axios.get('/api/v1/emr_options', {
            params: { emr_integration_credential_id: credentialId, source },
            signal: controller.signal,
            timeout: 10000,
          });
          if (!cancelled) {
            setOptionsBySource((current) => ({ ...current, [source]: response.data.options || [] }));
          }
        } catch {
          if (!cancelled && !controller.signal.aborted) setFailedSources((current) => ({ ...current, [source]: true }));
        } finally {
          if (!cancelled) setLoadingSources((current) => ({ ...current, [source]: false }));
        }
      }));
    };

    loadOptions();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [credentialId, fields]);

  const updateField = (fieldName, nextValue) => {
    const next = nextValue === undefined ? withoutField(value, fieldName) : { ...value, [fieldName]: nextValue };
    onChange(next);
  };

  // Choosing a strategy keeps any provider already selected as its fallback.
  // Choosing a person drops the strategy, and clearing removes both.
  const updateStrategyField = (fieldName, nextValue) => {
    if (typeof nextValue === 'string' && nextValue.startsWith(STRATEGY_VALUE_PREFIX)) {
      onChange({ ...value, [STRATEGY_KEY]: nextValue.slice(STRATEGY_VALUE_PREFIX.length) });
      return;
    }
    const next = withoutField(value, STRATEGY_KEY);
    onChange(nextValue === undefined ? withoutField(next, fieldName) : { ...next, [fieldName]: nextValue });
  };

  const isConfigured = (field) => Object.prototype.hasOwnProperty.call(value, field.internal_name)
    || (fieldStrategies(field).length > 0 && Object.prototype.hasOwnProperty.call(value, STRATEGY_KEY));

  const resetField = (field) => {
    const next = withoutField(value, field.internal_name);
    onChange(fieldStrategies(field).length > 0 ? withoutField(next, STRATEGY_KEY) : next);
  };

  const renderInput = (field) => {
    const name = field.internal_name;
    const fieldValue = value[name];
    if (!FIELD_COMPONENTS.has(field.component)) {
      if (!warnedComponents.has(field.component)) {
        warnedComponents.add(field.component);
        console.warn(`Unsupported auto-upload document defaults component: ${field.component}`);
      }
      return null;
    }

    if (field.component === 'checkbox') {
      return (
        <Select
          aria-label={field.label}
          value={fieldValue}
          onChange={(nextValue) => updateField(name, nextValue)}
        >
          <Option value={undefined}>Inherit</Option>
          <Option value>Yes</Option>
          <Option value={false}>No</Option>
        </Select>
      );
    }

    if (field.component === 'select') {
      const source = field.options_source;
      const options = optionsBySource[source] || [];
      const loading = loadingSources[source];
      const failed = failedSources[source];
      const strategies = fieldStrategies(field);
      if (failed) return <Alert type="error" showIcon message={`Could not load ${field.label.toLowerCase()} options. Try again later.`} />;
      if (!loading && source && options.length === 0 && strategies.length === 0) {
        return <Alert type="info" showIcon message="No active providers. Add them under Settings → Providers." />;
      }
      const strategy = strategies.length > 0 ? value[STRATEGY_KEY] : undefined;
      return (
        <Select
          aria-label={field.label}
          allowClear
          showSearch
          optionFilterProp="children"
          value={strategy ? `${STRATEGY_VALUE_PREFIX}${strategy}` : fieldValue}
          loading={loading}
          disabled={loading}
          placeholder={loading ? 'Loading providers...' : `Select ${field.label.toLowerCase()}`}
          onChange={(nextValue) => (strategies.length > 0 ? updateStrategyField(name, nextValue) : updateField(name, nextValue))}
        >
          {strategies.map((key) => (
            <Option key={`${STRATEGY_VALUE_PREFIX}${key}`} value={`${STRATEGY_VALUE_PREFIX}${key}`}>{STRATEGIES[key].label}</Option>
          ))}
          {options.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
        </Select>
      );
    }

    if (field.component === 'textarea') {
      return (
        <>
          <TextArea
            aria-label={field.label}
            maxLength={500}
            rows={3}
            value={fieldValue || ''}
            onChange={(event) => updateField(name, event.target.value)}
          />
          <div style={{ color: '#888', fontSize: 12, textAlign: 'right' }}>
            {`${(fieldValue || '').length}/500`}
          </div>
        </>
      );
    }

    return (
      <Input
        aria-label={field.label}
        value={fieldValue || ''}
        onChange={(event) => updateField(name, event.target.value)}
      />
    );
  };

  const renderStrategyFallback = (field) => {
    const strategy = value[STRATEGY_KEY];
    if (!fieldStrategies(field).includes(strategy)) return null;
    const options = optionsBySource[field.options_source] || [];
    return (
      <Form.Item key={`${field.internal_name}-fallback`} label="If not found, assign to" extra={STRATEGIES[strategy].fallbackHelp}>
        <Select
          aria-label="If not found, assign to"
          allowClear
          showSearch
          optionFilterProp="children"
          value={value[field.internal_name]}
          placeholder="Select a fallback provider or inbox"
          onChange={(nextValue) => updateField(field.internal_name, nextValue)}
        >
          {options.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
        </Select>
      </Form.Item>
    );
  };

  return (
    <>
      {providerError && <Alert style={{ marginBottom: 12 }} type="error" showIcon message={providerError} />}
      {fields.map((field) => {
        const input = renderInput(field);
        if (!input) return null;
        return (
          <React.Fragment key={field.internal_name}>
            <Form.Item
              label={field.label}
              extra={fieldExtra(field)}
            >
              {input}
              {isConfigured(field) && (
                <Button type="link" size="small" onClick={() => resetField(field)}>
                  Reset to inherit
                </Button>
              )}
            </Form.Item>
            {renderStrategyFallback(field)}
          </React.Fragment>
        );
      })}
    </>
  );
};

RuleDocumentDefaultsFields.propTypes = {
  fields: PropTypes.array.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  providerError: PropTypes.string,
};

RuleDocumentDefaultsFields.defaultProps = {
  credentialId: null,
  providerError: null,
};

export default RuleDocumentDefaultsFields;
